// src/pages/ProductDetail.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

const WAPP_NUMBER = "22800000000";
const SALES_EMAIL = "contact@mameche.tg";
const wapp = (txt) => `https://wa.me/${WAPP_NUMBER}?text=${encodeURIComponent(txt)}`;
const mailto = (subject, body) =>
  `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

// Styles d’images uniformes (héros + vignettes)
const styles = {
  heroWrap: {
    height: 420,
    background: "#f8f9fa",
    borderRadius: "0.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  heroImg: { maxWidth: "100%", maxHeight: "100%", objectFit: "cover" },
  thumbWrap: {
    height: 180,
    background: "#f8f9fa",
    borderBottom: "1px solid rgba(0,0,0,.075)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  thumbImg: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },
  svgPh: encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='600'>
      <rect width='100%' height='100%' fill='%23f8f9fa'/>
      <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'
        fill='%239aa0a6' font-family='Arial' font-size='20'>Image indisponible</text>
    </svg>`
  ),
};

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      const ref = doc(db, "products", id);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        setLoading(false);
        return;
      }
      const data = { id: snap.id, ...snap.data() };
      setP(data);

      // Produits similaires : d’abord par catégorie, sinon par département
      const col = collection(db, "products");
      let q;
      if (data.category) {
        q = query(col, where("category", "==", data.category), orderBy("createdAt", "desc"), limit(8));
      } else if (data.department) {
        q = query(col, where("department", "==", data.department), orderBy("createdAt", "desc"), limit(8));
      }
      if (q) {
        const relSnap = await getDocs(q);
        const arr = relSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((x) => x.id !== data.id)
          .slice(0, 4);
        setRelated(arr);
      }
      setLoading(false);
    };
    run();
  }, [id]);

  const deptLabel = useMemo(() => {
    if (!p?.department) return "";
    return p.department === "medical" ? "Médical" : "Industriel";
  }, [p]);

  // URL vers Contact (pré-remplissage)
  const contactURL = p
    ? `/contact?subjectType=${encodeURIComponent("Demande de devis")}` +
      `&dept=${encodeURIComponent(p.department || "medical")}` +
      `&product=${encodeURIComponent(p.title || "")}` +
      `&ref=${encodeURIComponent(p.ref || "")}`
    : "/contact";

  // JSON-LD SEO
  const jsonLd = p
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: p.title || "Produit",
        sku: p.ref || "",
        category: p.category || "",
        description: p.desc || "",
        brand: p.brand || "MAMECHE",
        image: p.imageUrl ? [p.imageUrl] : [],
      }
    : null;

  if (loading) {
    return (
      <div className="container py-4">
        <div className="alert alert-light border">Chargement…</div>
      </div>
    );
  }

  if (!p) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          Produit introuvable.{" "}
          <button className="btn btn-link p-0" onClick={() => nav(-1)}>
            ← Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      {/* JSON-LD */}
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}

      {/* Fil d’Ariane */}
      <nav className="small mb-3" aria-label="breadcrumb">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/">Accueil</Link>
          </li>
          {p.department && (
            <li className="breadcrumb-item">
              <Link to={`/${p.department}/products`}>{deptLabel}</Link>
            </li>
          )}
          <li className="breadcrumb-item active" aria-current="page">
            {p.title || "Détail produit"}
          </li>
        </ol>
      </nav>

      {/* En-tête */}
      <div className="row g-4 align-items-start">
        <div className="col-lg-6">
          <div className="card shadow-sm">
            <div style={styles.heroWrap}>
              {p.imageUrl ? (
                <img src={p.imageUrl} alt={p.title} style={styles.heroImg} />
              ) : (
                <img
                  alt="placeholder"
                  style={styles.heroImg}
                  src={`data:image/svg+xml;utf8,${styles.svgPh}`}
                />
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
            {p.department && (
              <span className={"badge " + (p.department === "medical" ? "bg-success" : "bg-secondary")}>
                {deptLabel}
              </span>
            )}
            {p.category && <span className="badge text-bg-light">{p.category}</span>}
            {p.ref && <span className="badge text-bg-light">Réf. {p.ref}</span>}
          </div>

          <h1 className="h3 fw-bold">{p.title || "Produit"}</h1>
          {p.brand && <div className="text-muted mb-2">Marque : {p.brand}</div>}
          <p className="text-muted">{p.desc || "Description sur demande."}</p>

          {/* Caractéristiques synthétiques (facultatives) */}
          <div className="row g-3 small">
            {p.model && (
              <div className="col-md-6">
                <div className="border rounded p-2">
                  Modèle
                  <br />
                  <strong>{p.model}</strong>
                </div>
              </div>
            )}
            {p.specs && (
              <div className="col-md-6">
                <div className="border rounded p-2">
                  Spécifications
                  <br />
                  <strong>{p.specs}</strong>
                </div>
              </div>
            )}
          </div>

          {/* CTAs */}
          <div className="d-flex flex-wrap gap-2 mt-4">
            <Link className="btn btn-primary" to={contactURL}>
              Demander un devis (formulaire)
            </Link>
            <a
              className="btn btn-success"
              href={wapp(`Bonjour MAMECHE, je souhaite un devis pour: ${p.title} (${p.ref ?? "—"}).`)}
              target="_blank"
              rel="noreferrer"
            >
              WhatsApp
            </a>
            <a
              className="btn btn-outline-secondary"
              href={mailto(
                `Demande de devis – ${p.title}`,
                `Bonjour,\n\nJe souhaite un devis pour la référence ${p.ref ?? "—"} (${p.title}).\nQuantité: __\nDélai souhaité: __\n\nCordialement,`
              )}
            >
              Email
            </a>
            {p.datasheetUrl && (
              <a className="btn btn-outline-primary" href={p.datasheetUrl} target="_blank" rel="noreferrer">
                Fiche technique (PDF)
              </a>
            )}
            <button
              className="btn btn-link text-decoration-none"
              onClick={() => {
                const shareData = {
                  title: p.title,
                  text: `${p.title} – Ref. ${p.ref ?? "—"} sur MAMECHE`,
                  url: window.location.href,
                };
                if (navigator.share) navigator.share(shareData);
                else {
                  navigator.clipboard.writeText(window.location.href);
                  alert("Lien copié dans le presse-papiers.");
                }
              }}
            >
              Partager
            </button>
          </div>
        </div>
      </div>

      {/* Similaires */}
      {related.length > 0 && (
        <div className="mt-5">
          <h3 className="h5 fw-bold mb-3">Produits similaires</h3>
          <div className="row g-3">
            {related.map((r) => (
              <div className="col-12 col-md-6 col-lg-3" key={r.id}>
                <div className="card h-100 shadow-sm">
                  <div style={styles.thumbWrap}>
                    {r.imageUrl ? (
                      <img src={r.imageUrl} alt={r.title} style={styles.thumbImg} />
                    ) : (
                      <img
                        alt="placeholder"
                        style={styles.thumbImg}
                        src={`data:image/svg+xml;utf8,${styles.svgPh}`}
                      />
                    )}
                  </div>
                  <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start">
                      {r.department && (
                        <span className={"badge " + (r.department === "medical" ? "bg-success" : "bg-secondary")}>
                          {r.department === "medical" ? "Médical" : "Industriel"}
                        </span>
                      )}
                      <small className="text-muted">Réf. {r.ref || "—"}</small>
                    </div>
                    <h6 className="mt-2 mb-1">{r.title}</h6>
                    <p className="text-muted small mb-3">{r.desc?.slice(0, 80)}</p>
                    <div className="mt-auto d-flex gap-2">
                      <Link className="btn btn-outline-primary btn-sm" to={`/product/${r.id}`}>
                        Détails
                      </Link>
                      <Link
                        className="btn btn-primary btn-sm"
                        to={
                          `/contact?subjectType=${encodeURIComponent("Demande de devis")}` +
                          `&dept=${encodeURIComponent(r.department || "medical")}` +
                          `&product=${encodeURIComponent(r.title || "")}` +
                          `&ref=${encodeURIComponent(r.ref || "")}`
                        }
                      >
                        Devis
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}